/**
 * ============================================================================
 * StatTile — Phase 6b frontend (Quality Metrics Dashboard v2)
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: oversized hero stat shown above the 12-metric grid. Four of these
 *          live at the top of the dashboard: open deviations, open CAPAs, doc
 *          overdue, training currency.
 *
 * VISUAL: editorial-style — a large numeral, a quiet label, and a tiny delta
 *  pill against yesterday. A traffic-light dot anchors the band so the
 *  display works at a glance.
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { DashboardHeroStat, TrafficLightBand } from './useDashboardQuery';
import styles from './QualityMetricsDashboard.module.css';

export interface StatTileProps {
  stat: DashboardHeroStat;
}

const BAND_DOT_CLASS: Record<TrafficLightBand, string> = {
  GREEN: styles.bandDotGreen,
  AMBER: styles.bandDotAmber,
  RED: styles.bandDotRed,
  UNKNOWN: styles.bandDotUnknown,
};

export function StatTile({ stat }: StatTileProps) {
  const { t, i18n } = useTranslation();
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  const label = t(`qualityMetrics.heroStat.${stat.key}.label`, {
    defaultValue: stat.label,
  });
  const bandLabel = t(`qualityMetrics.band.${stat.band}`, {
    defaultValue: stat.band,
  });

  const deltaText =
    stat.delta_vs_yesterday == null
      ? null
      : stat.delta_vs_yesterday > 0
        ? `▲ ${nf.format(stat.delta_vs_yesterday)}`
        : stat.delta_vs_yesterday < 0
          ? `▼ ${nf.format(Math.abs(stat.delta_vs_yesterday))}`
          : `▬ 0`;

  // Higher-is-bad metrics (deviations, capas, overdue) treat positive delta as
  // worse; training currency is higher-is-better. We don't know the polarity
  // generically, so we keep the colour neutral and let the band dot encode
  // health.
  return (
    <section
      className={styles.statTile}
      aria-label={t('qualityMetrics.heroStat.aria', {
        defaultValue: '{{label}}: {{value}}. Status {{band}}.',
        label,
        value: nf.format(stat.value),
        band: bandLabel,
      })}
    >
      <header className={styles.statTileHeader}>
        <span className={styles.statTileLabel}>{label}</span>
        <span
          className={`${styles.bandDot} ${BAND_DOT_CLASS[stat.band]}`}
          aria-hidden="true"
        />
      </header>
      <div className={styles.statTileValue}>{nf.format(stat.value)}</div>
      {deltaText && (
        <div className={styles.statTileDelta} aria-hidden="true">
          {deltaText}{' '}
          <span className={styles.statTileDeltaMuted}>
            {t('qualityMetrics.heroStat.deltaSuffix', {
              defaultValue: 'vs yesterday',
            })}
          </span>
        </div>
      )}
    </section>
  );
}
